/**
 * Crew mobile · Task detail (wireframe 08, phone A).
 *
 * Full-bleed mobile screen for a single task: a live timer, the site
 * context the crew needs on arrival, the status transition actions, and
 * the "this task" activity feed built from the status timeline.
 *
 * Photo / video / voice / punch capture (Pillar B.3, B.5) layer onto the actions row
 * here next; for now this is the real read + transition surface.
 *
 * Route: /today/tasks/:taskId (standalone — no AppShell sidebar).
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Hammer,
  Loader2,
  Map as MapIcon,
  MapPin,
  Mic,
  Navigation,
  Paperclip,
  Phone,
  Play,
  RotateCcw,
  Route,
  Truck,
  User,
  Wrench,
  X,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import type {
  Attachment,
  CrewTaskDetailResponse,
  CrewTaskTimelineEntry,
  TaskStatus,
  TaskType,
} from '@vari-crew/schemas';
import { phoneToTelHref } from '@vari-crew/schemas';
import { isQueuedNote, useAddTaskNote, useCrewTaskDetail } from '@/api/crew-today';
import { VehicleIssueCard } from '@/components/crew/VehicleIssueCard';
import { TaskChatPanel } from '@/components/TaskChatPanel';
import { isQueuedResult, useExtendTaskReminder, useTask, useTransitionTask } from '@/api/tasks';
import { useCurrentUser } from '@/auth/useCurrentUser';
import { RecapNotesCard } from '@/components/projects/RecapNotesCard';
import { EditTaskButton } from '@/components/projects/TaskEditor';
import { TaskTimesEditor } from '@/components/projects/TaskTimesEditor';
import { TaskStatusControl } from '@/components/projects/TaskStatusControl';
import {
  attachmentFileUrl,
  uploadPunchItemMedia,
  useProjectDocuments,
  useTaskAttachments,
  useUploadTaskMedia,
} from '@/api/attachments';
import { docKindLabel, docKindTint } from '@/lib/docKind';
import { useCreatePunchItem, usePunchItems } from '@/api/punch-items';
import { CopyButton } from '@/components/ui/CopyButton';
import { destinationUrl, journeyUrl } from '@/lib/maps';
import { useDesignPackage } from '@/api/design-package';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { SiteMap } from '@/components/ui/SiteMap';
import { cn } from '@/lib/cn';
import { useDictation } from '@/lib/speech';

const TYPE_ICON: Record<TaskType, LucideIcon> = {
  pickup: Truck,
  install: Hammer,
  punch: Wrench,
  labor: User,
};

const STATUS_BADGE: Record<TaskStatus, string> = {
  scheduled: 'bg-status-scheduled text-status-scheduled-fg',
  en_route: 'bg-status-enroute text-status-enroute-fg',
  on_site: 'bg-status-onsite text-status-onsite-fg',
  in_progress: 'bg-status-onsite text-status-onsite-fg',
  paused: 'bg-status-scheduled text-status-scheduled-fg',
  blocked: 'bg-status-blocked text-status-blocked-fg',
  complete: 'bg-status-complete text-status-complete-fg',
  cancelled: 'bg-fill text-ink-dim',
};

function statusLabel(s: TaskStatus): string {
  return s.replace('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function timeOnly(iso: string | null): string {
  if (!iso) return '';
  return format(parseISO(iso), 'h:mma').toLowerCase();
}

/** Deep-link to the device's native maps app for turn-by-turn directions. */
function directionsUrl(addr: {
  line1: string;
  city: string;
  state: string;
  postal_code: string;
  latitude: number | null;
  longitude: number | null;
}): string {
  const dest =
    addr.latitude != null && addr.longitude != null
      ? `${addr.latitude},${addr.longitude}`
      : `${addr.line1}, ${addr.city}, ${addr.state} ${addr.postal_code}`;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`;
}

/** HH:MM:SS for a live/elapsed duration. */
function formatClock(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/** Re-render every second while `active` so a running timer ticks. */
function useNowTicker(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);
  return now;
}

export function CrewTaskDetail() {
  const { taskId } = useParams<{ taskId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useCrewTaskDetail(taskId);
  const { data: me } = useCurrentUser();
  const canManage = me?.role === 'supervisor' || me?.role === 'admin';
  // Status transitions are field-execution actions — assigned crew run them, and
  // supervisors/admins can adjust on a crew's behalf. PMs/sales get a read-only
  // view (mirrors the API, which gates the transition to crew/supervisor/admin).
  const canChangeStatus = me?.role === 'crew' || canManage;
  // Supervisor/admin override of the recorded start/finish times (Surface A).
  const [editingTimes, setEditingTimes] = useState(false);

  // Retrace wherever the task was opened from (Today, My week, the project,
  // the calendar); fall back to Today on a cold/direct load.
  const goBack = () => (location.key !== 'default' ? navigate(-1) : navigate('/today'));

  // Deep link from a crew-chat mention email (…/tasks/:id#crew-chat) lands here —
  // scroll the chat into view once it's painted (the SPA won't honor the hash on
  // its own). The sections below the timer (plans, docs, punch, timeline) load
  // async and grow the page after first paint, so retry until the layout settles.
  useEffect(() => {
    if (!data || location.hash !== '#crew-chat') return;
    const timers = [250, 800, 1600].map((ms) =>
      window.setTimeout(() => {
        document.getElementById('crew-chat')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, ms),
    );
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [data, location.hash]);

  if (isLoading) {
    return (
      <div className="h-full grid place-items-center bg-fill-soft">
        <Loader2 className="w-5 h-5 animate-spin text-ink-faint" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-full bg-fill-soft">
        <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4">
          <BackBar onBack={goBack} />
          <div className="bg-bg rounded-2xl border border-line p-8 text-center text-sm text-ink-dim mt-4">
            {(error as Error | undefined)?.message ?? 'This task could not be found.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-fill-soft pb-10">
      <div className="max-w-md md:max-w-2xl mx-auto px-4 pt-4 space-y-4">
        {/* History-aware: a supervisor arriving from a project must not get
            bounced to /today (which shows them an empty crew state). */}
        <BackBar onBack={goBack} />
        <Header data={data} />
        {canManage && <SupervisorTaskBar taskId={data.id} />}
        <TimerCard
          data={data}
          canAct={canChangeStatus}
          canManage={canManage}
          onAdjust={() => setEditingTimes(true)}
        />
        {editingTimes && data.status === 'complete' && (
          <TaskTimesEditor
            taskId={data.id}
            startedAt={data.started_at}
            completedAt={data.completed_at}
            scheduledStart={data.scheduled_start}
            scheduledEnd={data.scheduled_end}
            onClose={() => setEditingTimes(false)}
          />
        )}
        {/* Revert / change — the big buttons above only step forward; this lets a
            crew member undo an accidental tap (or a supervisor set any status). */}
        {canChangeStatus && (
          <ChangeStatusRow taskId={data.id} status={data.status} canManage={canManage} />
        )}
        {data.type === 'pickup' && data.pickup_locations.length > 0 && (
          <PickupLocationsCard data={data} />
        )}
        <SiteCard data={data} />
        <InstructionsCard data={data} />
        <VehicleIssueCard
          vehicleId={data.vehicle_id}
          vehicleLabel={data.vehicle_label}
          taskId={data.id}
        />
        <PlansCard projectId={data.project_id} />
        <TaskDocumentsCard projectId={data.project_id} />
        {data.crew.length > 0 && <CrewCard data={data} />}
        <UpdateComposer taskId={data.id} />
        <PunchCard taskId={data.id} />
        <ActivityFeed taskId={data.id} timeline={data.timeline} />
        {/* Vari-crew-only chat (demo #7) — separate from the Updates above, which
            the requester can see. Hidden from PM/sales here (they're also blocked
            server-side on /tasks/:id/messages); mentions email teammates a link. */}
        {(me?.role === 'crew' || canManage) && <TaskChatPanel scope={{ taskId: data.id }} />}
        {/* Recap notes (demo #9) — capture photos + context for the closeout as
            you work; project-scoped so they roll up across every task. */}
        {(me?.role === 'crew' || canManage) && (
          <RecapNotesCard projectId={data.project_id} variant="capture" />
        )}
        <button
          onClick={() => navigate(`/projects/${data.project_id}`)}
          className="flex items-center justify-between w-full bg-bg rounded-2xl border border-line p-4 text-sm text-ink-dim hover:bg-fill-soft"
        >
          <span>Open full project · {data.project_client_name}</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function BackBar({ onBack }: { onBack: () => void }) {
  return (
    <button
      onClick={onBack}
      className="flex items-center gap-1.5 text-sm text-ink-dim hover:text-ink"
    >
      <ArrowLeft className="w-4 h-4" /> Back
    </button>
  );
}

/**
 * Supervisor-only edit affordance on the crew task screen. Crew get a
 * read-only work surface; a supervisor/admin can edit or remove the task in
 * place (same dialog as the project workspace). The crew payload omits
 * crew_size_needed, so we fetch the full task — only mounted for supervisors,
 * so the extra request never fires for crew. After a delete the task is gone,
 * so we return to Today rather than sit on a 404.
 */
function SupervisorTaskBar({ taskId }: { taskId: string }) {
  const navigate = useNavigate();
  const { data: task } = useTask(taskId);
  if (!task) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-3 flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase tracking-widest text-ink-faint">Supervisor</span>
      <EditTaskButton task={task} variant="button" onDeleted={() => navigate('/today')} />
    </div>
  );
}

function Header({ data }: { data: CrewTaskDetailResponse }) {
  const Icon = TYPE_ICON[data.type];
  const subtype =
    data.type === 'pickup'
      ? data.pickup_subtype
      : data.type === 'install'
        ? data.install_subtype
        : null;
  return (
    <header className="flex items-start justify-between gap-3">
      <div className="flex items-start gap-2.5 min-w-0">
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0 text-ink-faint" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-ink-faint">
            Task {data.step_index} of {data.step_count}
            {data.viewer_is_lead && ' · you lead'}
          </div>
          <h1 className="text-xl font-semibold leading-tight">{data.title}</h1>
          <div className="text-xs text-ink-dim mt-0.5 truncate">
            {data.project_title}
            {subtype && ` · ${subtype}`}
          </div>
        </div>
      </div>
      <span
        className={cn(
          'text-[11px] font-medium px-2 py-1 rounded-full whitespace-nowrap',
          STATUS_BADGE[data.status],
        )}
      >
        {statusLabel(data.status)}
      </span>
    </header>
  );
}

/** Crew can undo a mis-tapped Finish for this long after completing — mirrors the
 *  server's notification debounce (TASK_STATUS_NOTIFY_DELAY_MS) so the pending
 *  "completed" ping is suppressed, not sent (demo #7). */
const UNDO_GRACE_MS = 2 * 60 * 1000;

function TimerCard({
  data,
  canAct,
  canManage,
  onAdjust,
}: {
  data: CrewTaskDetailResponse;
  canAct: boolean;
  canManage: boolean;
  onAdjust: () => void;
}) {
  const running = data.status === 'in_progress';
  const completedMs = data.completed_at ? parseISO(data.completed_at).getTime() : null;
  // Keep the clock ticking through the undo grace window so the Undo button
  // auto-hides the moment it lapses.
  const recentlyComplete =
    data.status === 'complete' && completedMs != null && Date.now() - completedMs < UNDO_GRACE_MS;
  const now = useNowTicker(running || recentlyComplete);
  const canUndo =
    data.status === 'complete' && completedMs != null && now - completedMs < UNDO_GRACE_MS;
  const transition = useTransitionTask(data.id);
  const extendReminder = useExtendTaskReminder(data.id);

  function extend(minutes: number) {
    extendReminder.mutate(minutes, {
      onSuccess: () =>
        toast.success(`Got it — we'll remind the crew again in ${minutes} minutes`),
      onError: (err) => toast.error(`Couldn't extend: ${err.message}`),
    });
  }

  // Crew run a two-button flow (Start / Finish) — demo #7. No GPS/ETA capture
  // (the en route stage + public ETA were retired); status changes still queue
  // offline.
  function advance(to: 'in_progress' | 'complete') {
    transition.mutate(
      { to },
      {
        onSuccess: (res) =>
          isQueuedResult(res)
            ? toast.success(
                `Saved offline — "${statusLabel(to).toLowerCase()}" will sync when you reconnect`,
              )
            : toast.success(`Marked ${statusLabel(to).toLowerCase()}`),
        onError: (err) => toast.error(`Failed: ${err.message}`),
      },
    );
  }

  // Headline + big number depend on where the task is in its lifecycle.
  let eyebrow: string;
  let big: string;
  if (running && data.started_at) {
    eyebrow = `Started ${timeOnly(data.started_at)}`;
    big = formatClock(now - parseISO(data.started_at).getTime());
  } else if (data.status === 'complete' && data.started_at && data.completed_at) {
    eyebrow = `Completed ${timeOnly(data.completed_at)}`;
    // Counted worked time, computed server-side (taskActualMinutes): the recorded
    // window capped per business day — same number as the recap. Fall back to the
    // raw span only if the server couldn't derive it.
    big =
      data.worked_minutes != null
        ? formatClock(data.worked_minutes * 60_000)
        : formatClock(
            parseISO(data.completed_at).getTime() - parseISO(data.started_at).getTime(),
          );
  } else if (data.status === 'paused' && data.started_at) {
    eyebrow = `Paused · started ${timeOnly(data.started_at)}`;
    big = '—';
  } else if (data.scheduled_start) {
    eyebrow = `Scheduled · ${format(parseISO(data.scheduled_start), 'EEE, MMM d')}`;
    big = `${timeOnly(data.scheduled_start)}${data.scheduled_end ? ` – ${timeOnly(data.scheduled_end)}` : ''}`;
  } else {
    eyebrow = 'Not started';
    big = '—';
  }

  const onsite = ['en_route', 'on_site', 'in_progress', 'paused'].includes(data.status);

  return (
    <div
      className={cn(
        'rounded-2xl p-5 text-center',
        onsite ? 'bg-status-onsite text-status-onsite-fg' : 'bg-bg border border-line',
      )}
    >
      <div className="text-[11px] uppercase tracking-widest opacity-80">{eyebrow}</div>
      <div className="text-3xl font-bold tracking-tight tabular-nums mt-1">{big}</div>
      {/* Once underway, show the committed finish time so the crew know their
          window at a glance (the clock above counts up; this is the target). */}
      {['in_progress', 'on_site', 'paused'].includes(data.status) && data.scheduled_end && (
        <div className="text-[11px] uppercase tracking-widest opacity-80 mt-1">
          Scheduled finish · {timeOnly(data.scheduled_end)}
        </div>
      )}

      {/* h-11 (44px): glove/sunlight floor for the field's primary controls.
          Hidden for non-actors (PM/sales) — status is a crew/supervisor action. */}
      {canAct && (
      <div className="flex gap-2 mt-4">
        {/* Two-button crew flow (demo #7): Start a scheduled task, Finish when
            done. The en route / on site / pause stages were retired. */}
        {(data.status === 'scheduled' || data.status === 'en_route') && (
          <Button size="sm" className="flex-1 h-11" onClick={() => advance('in_progress')} disabled={transition.isPending}>
            <Play className="w-3.5 h-3.5" /> Start
          </Button>
        )}
        {(data.status === 'in_progress' || data.status === 'on_site' || data.status === 'paused') && (
          <Button size="sm" className="flex-1 h-11" onClick={() => advance('complete')} disabled={transition.isPending}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Finish
          </Button>
        )}
      </div>
      )}

      {/* Need more time? Extend the ending-soon reminder by a set interval
          (demo #8). Doesn't change the committed end time — the clock runs until
          Finish; this just schedules one more nudge. */}
      {canAct &&
        (data.status === 'in_progress' || data.status === 'on_site' || data.status === 'paused') && (
          <div className="mt-3">
            <div className="text-[11px] uppercase tracking-widest opacity-80 mb-1">
              Need more time?
            </div>
            <div className="flex gap-2">
              {[15, 30, 45, 60].map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant="outline"
                  className="flex-1 h-9"
                  onClick={() => extend(m)}
                  disabled={extendReminder.isPending}
                >
                  +{m}m
                </Button>
              ))}
            </div>
          </div>
        )}

      {data.status === 'blocked' && (
        <div className="flex items-center justify-center gap-2 mt-2 text-sm">
          <AlertTriangle className="w-4 h-4" /> Blocked — supervisor notified.
        </div>
      )}
      {data.status === 'complete' && (
        <div className="mt-2 flex flex-col items-center gap-2 text-sm text-status-complete-fg">
          <div className="flex items-center justify-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Task complete
          </div>
          {/* Grace-window undo of a mis-tapped Finish (demo #7) — auto-hides after
              ~2 min; the server enforces the same window. */}
          {canAct && canUndo && (
            <Button
              size="sm"
              variant="outline"
              className="h-9"
              onClick={() => advance('in_progress')}
              disabled={transition.isPending}
            >
              <RotateCcw className="w-3.5 h-3.5" /> Undo finish
            </Button>
          )}
          {canManage && (
            <Button size="sm" variant="outline" className="h-9" onClick={onAdjust}>
              <Clock className="w-3.5 h-3.5" /> Adjust times
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * "Change status" row — the escape hatch beside the forward action buttons.
 * The TimerCard only advances the lifecycle; this exposes the full set of valid
 * transitions (including reverts) so a crew member can undo a mis-tap and a
 * supervisor can set any status. The control itself mirrors the backend guard.
 */
function ChangeStatusRow({
  taskId,
  status,
  canManage,
}: {
  taskId: string;
  status: TaskStatus;
  canManage: boolean;
}) {
  return (
    <div className="bg-bg rounded-2xl border border-line p-3 flex items-center justify-between gap-3">
      <span className="text-xs text-ink-dim">Tapped the wrong status? Change it here.</span>
      <TaskStatusControl taskId={taskId} status={status} canManage={canManage} />
    </div>
  );
}

function PickupLocationsCard({ data }: { data: CrewTaskDetailResponse }) {
  const stops = data.pickup_locations;
  const site = data.site_address;
  // Full journey: every pickup, in order, then the install/site.
  const journey = site ? journeyUrl([...stops, site]) : '';
  return (
    <div className="bg-bg rounded-2xl border border-line p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] uppercase tracking-widest text-ink-faint">
          {stops.length > 1 ? `Pickups · ${stops.length} stops` : 'Pickup location'}
        </div>
        {journey && (
          <a
            href={journey}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-accent font-medium text-sm whitespace-nowrap"
          >
            <Route className="w-3.5 h-3.5" /> Map full journey
          </a>
        )}
      </div>
      <ol className="space-y-2">
        {stops.map((loc, i) => {
          const url = destinationUrl(loc);
          const addrLine = [
            loc.line1,
            [loc.city, loc.state].filter(Boolean).join(', '),
            loc.postal_code,
          ]
            .filter(Boolean)
            .join(' · ');
          const copyVal = [loc.name, loc.line1, [loc.city, loc.state].filter(Boolean).join(', '), loc.postal_code]
            .filter(Boolean)
            .join(', ');
          return (
            <li
              key={i}
              className="flex items-start justify-between gap-2 text-sm border-b border-line last:border-0 pb-2 last:pb-0"
            >
              <div className="flex items-start gap-2 min-w-0">
                <span className="mt-0.5 inline-flex w-5 h-5 items-center justify-center rounded-full bg-fill-soft text-[11px] font-medium shrink-0">
                  {i + 1}
                </span>
                <div className="min-w-0">
                  <div className="font-medium truncate">{loc.name}</div>
                  {addrLine && <div className="text-xs text-ink-faint truncate">{addrLine}</div>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {url && (
                  <a
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center gap-1 text-accent font-medium whitespace-nowrap"
                  >
                    <Navigation className="w-3.5 h-3.5" /> Map
                  </a>
                )}
                {copyVal && <CopyButton value={copyVal} label="Copy pickup address" />}
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function SiteCard({ data }: { data: CrewTaskDetailResponse }) {
  const addr = data.site_address;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4 space-y-3">
      {addr && (
        <div className="flex items-start justify-between gap-2 text-sm">
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 mt-0.5 text-ink-faint flex-shrink-0" />
            <div>
              {addr.line1}
              {addr.line2 && <>, {addr.line2}</>}
              <br />
              {addr.city}, {addr.state} {addr.postal_code}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={directionsUrl(addr)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1 text-accent font-medium whitespace-nowrap"
            >
              <Navigation className="w-3.5 h-3.5" /> Directions
            </a>
            <CopyButton
              value={[addr.line1, addr.line2, `${addr.city}, ${addr.state} ${addr.postal_code}`]
                .filter(Boolean)
                .join(', ')}
              label="Copy address"
            />
          </div>
        </div>
      )}
      {data.site_contact_name && (
        <div className="flex items-center justify-between gap-2 text-sm">
          <div className="flex items-center gap-2 text-ink-dim min-w-0">
            <User className="w-4 h-4 text-ink-faint shrink-0" />
            <span className="truncate">{data.site_contact_name}</span>
          </div>
          {data.site_contact_phone && (
            <div className="flex items-center gap-1 shrink-0">
              <a
                href={phoneToTelHref(data.site_contact_phone) ?? `tel:${data.site_contact_phone}`}
                className="flex items-center gap-1 text-accent font-medium tabular-nums"
              >
                <Phone className="w-3.5 h-3.5" /> {data.site_contact_phone}
              </a>
              <CopyButton value={data.site_contact_phone} label="Copy phone number" />
            </div>
          )}
        </div>
      )}
      {data.sap_rma_reference && (
        <div className="text-[11px] font-mono text-ink-faint">RMA · {data.sap_rma_reference}</div>
      )}
      <SiteMap
        latitude={addr?.latitude ?? null}
        longitude={addr?.longitude ?? null}
        label="Task site"
        expandable
        className="h-40"
      />
      {!addr && !data.site_contact_name && !data.sap_rma_reference && (
        <div className="text-sm text-ink-faint">No site info on file.</div>
      )}
    </div>
  );
}

/** Task instructions / notes — its own container so they aren't buried under the
 *  site map. Sits below the site card and above the crew card. */
function InstructionsCard({ data }: { data: CrewTaskDetailResponse }) {
  if (!data.description) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4 space-y-1">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint">Instructions</div>
      <div className="text-sm text-ink-dim whitespace-pre-wrap">{data.description}</div>
    </div>
  );
}

function PlansCard({ projectId }: { projectId: string }) {
  const navigate = useNavigate();
  const { data: pkg } = useDesignPackage(projectId);
  if (!pkg) return null; // no design package → nothing to show
  const pageCount = pkg.pages?.length ?? 0;
  return (
    <button
      onClick={() => navigate(`/projects/${projectId}/plans`)}
      className="flex items-center justify-between w-full bg-bg rounded-2xl border border-line p-4 hover:bg-fill-soft text-left"
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <MapIcon className="w-5 h-5 text-ink-faint flex-shrink-0" />
        <div className="min-w-0">
          <div className="text-sm font-medium">Plans &amp; docs</div>
          <div className="text-xs text-ink-dim truncate">
            Floorplan, 3D &amp; schedule{pageCount ? ` · ${pageCount} pages` : ''}
          </div>
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-ink-faint flex-shrink-0" />
    </button>
  );
}

/**
 * Project paperwork (sales order / packing slip / design package / other) so
 * the crew can open the originals from the task. Distinct from the Plans card,
 * which is the interactive design-package viewer. Hidden when none exist.
 */
function TaskDocumentsCard({ projectId }: { projectId: string }) {
  const { data } = useProjectDocuments(projectId);
  const docs = data?.data ?? [];
  if (docs.length === 0) return null;
  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">Documents</div>
      <ul className="space-y-1">
        {docs.map((d) => (
          <li key={d.id}>
            <a
              href={attachmentFileUrl(d)}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2.5 text-sm py-1.5 -mx-1 px-1 rounded-lg hover:bg-fill-soft"
            >
              <FileText className="w-4 h-4 text-ink-faint shrink-0" />
              <span className="flex-1 min-w-0 truncate">{d.original_filename}</span>
              <span
                className={cn(
                  'text-[10px] px-1.5 py-0.5 rounded border shrink-0',
                  docKindTint(d),
                )}
              >
                {docKindLabel(d)}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** A small photo/video tile used in the activity feed. */
function MediaThumb({ a }: { a: Attachment }) {
  const isVideo = a.mime_type.startsWith('video/');
  const box = 'w-20 h-20 rounded-lg overflow-hidden border border-line bg-fill-soft flex-shrink-0';
  if (isVideo) {
    return (
      <video
        src={attachmentFileUrl(a)}
        className={`${box} object-cover`}
        controls
        playsInline
        preload="metadata"
      />
    );
  }
  return (
    <a href={attachmentFileUrl(a)} target="_blank" rel="noreferrer" className={`block ${box}`}>
      <img
        src={attachmentFileUrl(a)}
        alt={a.original_filename}
        loading="lazy"
        className="w-full h-full object-cover"
      />
    </a>
  );
}

/**
 * Unified "Add update" composer (Workspace.2): one entry point for a note,
 * photos/videos, and (optionally) a punch item. Attached media link to the note
 * (or the punch), so the activity feed shows them together.
 */
function UpdateComposer({ taskId, audience }: { taskId: string; audience?: string }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const [usedVoice, setUsedVoice] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [asPunch, setAsPunch] = useState(false);
  const [blocks, setBlocks] = useState(true);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const addNote = useAddTaskNote(taskId);
  const uploadMedia = useUploadTaskMedia(taskId);
  const createPunch = useCreatePunchItem(taskId);
  const { supported, listening, toggle } = useDictation((t) => {
    setText((prev) => (prev ? `${prev} ${t}` : t));
    setUsedVoice(true);
  });

  const previews = useMemo(
    () => files.map((f) => ({ url: URL.createObjectURL(f), isVideo: f.type.startsWith('video/') })),
    [files],
  );
  useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

  function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    setFiles((prev) => [...prev, ...Array.from(e.target.files ?? [])]);
    e.target.value = '';
  }

  const body = text.trim();
  const canSave = (body.length > 0 || files.length > 0) && !saving;

  async function save() {
    if (saving) return;
    setSaving(true);
    try {
      let punchRerouted = false;
      if (asPunch) {
        if (!body) {
          toast.error('Describe the issue to flag a punch item.');
          return;
        }
        const punch = await createPunch.mutateAsync({
          title: body.length > 120 ? `${body.slice(0, 117)}…` : body,
          description: body.length > 120 ? body : null,
          blocks_recap: blocks,
        });
        // The API routes items flagged from a non-punch task onto the
        // project's punch task (VCS-101) — without saying so, the item
        // "vanishes" from this screen and crews flag it twice.
        punchRerouted = punch.task_id !== taskId;
        for (const f of files) await uploadPunchItemMedia(punch.id, f);
      } else if (body) {
        const note = await addNote.mutateAsync({ body, source: usedVoice ? 'voice' : 'text' });
        if (isQueuedNote(note)) {
          // Offline: the note text is queued and will sync; photos need a
          // connection (they can't link to a note that has no id yet), so
          // keep them staged for a retry instead of silently dropping them.
          setText('');
          setUsedVoice(false);
          if (files.length > 0) {
            toast.warning(
              "You're offline — the note is saved and will sync, but photos need a connection. They're still attached below; save again once you're back online.",
            );
          } else {
            toast.success('Note saved offline — will sync when you reconnect');
          }
          return;
        }
        for (const f of files) await uploadMedia.mutateAsync({ file: f, noteId: note.id });
      } else {
        for (const f of files) await uploadMedia.mutateAsync({ file: f });
      }
      qc.invalidateQueries({ queryKey: ['crew-task-detail', taskId] });
      qc.invalidateQueries({ queryKey: ['tasks', taskId, 'attachments'] });
      setText('');
      setFiles([]);
      setUsedVoice(false);
      setAsPunch(false);
      setBlocks(true);
      toast.success(
        asPunch
          ? punchRerouted
            ? "Punch item flagged — it's on the project's punch list"
            : 'Punch item flagged'
          : 'Update added',
      );
    } catch (err) {
      toast.error(`Failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-bg rounded-2xl border border-line p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-widest text-ink-faint">Add update</span>
        {audience && (
          <span className="inline-flex items-center gap-1 rounded-full bg-accent/10 text-accent px-2 py-0.5 text-[10px] font-medium">
            <Eye className="w-3 h-3" /> {audience}
          </span>
        )}
      </div>
      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder={asPunch ? "What's the issue? (e.g. WS-14 wrong fabric)" : 'Note, observation, or update…'}
        rows={3}
        className="w-full rounded-lg border border-line bg-fill-soft p-2.5 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-accent"
      />

      {previews.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {previews.map((p, i) => (
            <div
              key={i}
              className="relative w-16 h-16 rounded-lg overflow-hidden border border-line bg-fill-soft"
            >
              {p.isVideo ? (
                <video src={p.url} className="w-full h-full object-cover" muted />
              ) : (
                <img src={p.url} alt="" className="w-full h-full object-cover" />
              )}
              <button
                type="button"
                onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                className="absolute top-0.5 right-0.5 bg-ink/70 text-bg rounded-full p-0.5"
                aria-label="Remove"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*,video/*"
        capture="environment"
        multiple
        className="hidden"
        onChange={onPick}
      />

      <label className="flex items-center gap-2 text-sm text-ink-dim">
        <input
          type="checkbox"
          checked={asPunch}
          onChange={(e) => setAsPunch(e.target.checked)}
          className="accent-status-blocked-fg"
        />
        Flag as punch item
      </label>
      {asPunch && (
        <label className="flex items-center gap-2 text-sm text-ink-dim pl-6">
          <input
            type="checkbox"
            checked={blocks}
            onChange={(e) => setBlocks(e.target.checked)}
            className="accent-status-blocked-fg"
          />
          Blocks closeout
        </label>
      )}

      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={() => inputRef.current?.click()}>
          <Paperclip className="w-3.5 h-3.5" /> Photo / video
        </Button>
        {supported && (
          <Button type="button" size="sm" variant={listening ? 'default' : 'outline'} onClick={toggle}>
            <Mic className="w-3.5 h-3.5" /> {listening ? 'Listening…' : 'Dictate'}
          </Button>
        )}
        <Button size="sm" className="flex-1 min-w-[7rem]" onClick={save} disabled={!canSave}>
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
          {asPunch ? 'Flag punch' : 'Save update'}
        </Button>
      </div>
    </div>
  );
}

function CrewCard({ data }: { data: CrewTaskDetailResponse }) {
  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">Crew</div>
      <ul className="space-y-2">
        {data.crew.map((c, i) => (
          <li key={i} className="flex items-center gap-2.5 text-sm">
            <Avatar name={c.display_name} src={c.avatar_url} className="w-7 h-7" />
            <span className="font-medium">{c.display_name}</span>
            <span className="text-ink-faint text-xs">{c.crew_role}</span>
            {c.is_lead && (
              <span className="ml-auto text-[10px] uppercase tracking-wide text-accent font-semibold">
                Lead
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Punch items are flagged via the unified composer; this lists them (open +
// closed) once any exist, on every task type. Hidden when there are none.
function PunchCard({ taskId }: { taskId: string }) {
  const { data } = usePunchItems(taskId);
  const items = data?.data ?? [];
  if (items.length === 0) return null;

  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-2">
        Punch items · {items.length}
      </div>
      <ul className="space-y-2">
        {items.map((p) => (
          <li key={p.id} className="flex items-start gap-2 text-sm">
            <AlertTriangle
              className={cn(
                'w-3.5 h-3.5 mt-0.5 flex-shrink-0',
                p.status === 'closed' ? 'text-ink-faint' : 'text-status-blocked-fg',
              )}
            />
            <div className="min-w-0 flex-1">
              <div
                className={cn('font-medium', p.status === 'closed' && 'line-through text-ink-faint')}
              >
                {p.title}
              </div>
              {p.description && <div className="text-xs text-ink-dim">{p.description}</div>}
            </div>
            {p.blocks_recap && p.status !== 'closed' && (
              <span className="text-[10px] uppercase tracking-wide text-status-blocked-fg font-semibold whitespace-nowrap">
                Blocks closeout
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimeStamp({ at }: { at: string }) {
  return (
    <span className="text-ink-faint font-normal text-xs">
      {format(parseISO(at), 'h:mma').toLowerCase()}
    </span>
  );
}

/**
 * Unified activity feed (Workspace.2): status changes, notes (with their
 * attached photos/videos inline), and standalone media — newest first.
 */
function ActivityFeed({ taskId, timeline }: { taskId: string; timeline: CrewTaskTimelineEntry[] }) {
  const { data } = useTaskAttachments(taskId);
  const media = useMemo(() => data?.data ?? [], [data]);

  const mediaByNote = useMemo(() => {
    const m = new Map<string, Attachment[]>();
    for (const a of media) {
      if (a.note_id) {
        const list = m.get(a.note_id) ?? [];
        list.push(a);
        m.set(a.note_id, list);
      }
    }
    return m;
  }, [media]);

  const items = useMemo(() => {
    const list: { at: string; key: string; node: ReactNode }[] = [];
    for (const e of timeline) {
      if (e.kind === 'status') {
        list.push({
          at: e.occurred_at,
          key: e.id,
          node: (
            <>
              <div className="font-medium">
                <TimeStamp at={e.occurred_at} /> {statusLabel(e.to_status)}
              </div>
              {e.actor_name && <div className="text-xs text-ink-faint">by {e.actor_name}</div>}
              {e.note && <div className="text-xs text-ink-dim mt-0.5">{e.note}</div>}
            </>
          ),
        });
      } else {
        const m = mediaByNote.get(e.id) ?? [];
        list.push({
          at: e.occurred_at,
          key: e.id,
          node: (
            <>
              <div className="font-medium">
                <TimeStamp at={e.occurred_at} /> Note{e.source === 'voice' ? ' 🎙' : ''}
              </div>
              {e.actor_name && <div className="text-xs text-ink-faint">by {e.actor_name}</div>}
              <div className="text-xs text-ink-dim mt-0.5 whitespace-pre-wrap">{e.body}</div>
              {m.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {m.map((a) => (
                    <MediaThumb key={a.id} a={a} />
                  ))}
                </div>
              )}
            </>
          ),
        });
      }
    }
    // Standalone media (not tied to a note or punch) — their own entries.
    for (const a of media) {
      if (!a.note_id && !a.punch_item_id) {
        list.push({
          at: a.uploaded_at,
          key: a.id,
          node: (
            <>
              <div className="font-medium">
                <TimeStamp at={a.uploaded_at} />{' '}
                {a.mime_type.startsWith('video/') ? 'Video' : 'Photo'}
              </div>
              <div className="mt-2">
                <MediaThumb a={a} />
              </div>
            </>
          ),
        });
      }
    }
    return list.sort((x, y) => y.at.localeCompare(x.at));
  }, [timeline, media, mediaByNote]);

  return (
    <div className="bg-bg rounded-2xl border border-line p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-faint mb-3">Activity</div>
      {items.length === 0 ? (
        <div className="text-sm text-ink-faint">No activity yet.</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => (
            <li key={it.key} className="border-l-2 border-line pl-3 text-sm">
              {it.node}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Re-export the task sections so the flag-gated tabbed variant
// (CrewTaskDetailTabbed) can compose the exact same pieces — no behavior change
// to this screen, and no logic duplicated between the two.
export {
  BackBar,
  SupervisorTaskBar,
  Header as TaskHeader,
  TimerCard,
  ChangeStatusRow,
  PickupLocationsCard,
  SiteCard,
  InstructionsCard,
  PlansCard,
  TaskDocumentsCard,
  UpdateComposer,
  CrewCard,
  PunchCard,
  ActivityFeed,
};
